import { useEffect, useState } from "react";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import type { SavedMessage } from "../sync/types";
import { MessageItem } from "./MessageItem";
import { EmptyState, Page } from "./ui";
import { locale, t } from "../i18n";

// SavedView lists the messages one saved for later, newest saved first.
export function SavedView() {
  const session = useSession();
  const state = useLantern();
  const [list, setList] = useState<SavedMessage[] | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    session.api.saved().then(
      (r) => {
        if (!live) return;
        setList(r.saved);
        setMore(r.saved.length >= 30);
      },
      (err) => setError(String(err)),
    );
    return () => {
      live = false;
    };
  }, [session, state.savedNotices]);

  async function loadMore() {
    const last = list?.at(-1);
    if (!last) return;
    const r = await session.api.saved(last.saved_at);
    setList([...(list ?? []), ...r.saved]);
    setMore(r.saved.length >= 30);
  }

  // Unsaved here, a message stays in view until the page reloads, so it can
  // be saved again.
  return (
    <Page className="saved-view" narrow title={t("Saved for later")} description={t("Messages you saved, newest first. Only you see this list.")}>
      {error && <p className="error">{error}</p>}
      {list && list.length === 0 && (
        <EmptyState icon="🔖" title={t("Nothing saved")}>
          {t("Hover over a message and choose Save for later to keep it here.")}
        </EmptyState>
      )}
      {list && list.length > 0 && (
        <ul className="card saved-list">
          {list.map(({ message: m, saved_at }) => (
            <li key={m.id} className={state.saved.has(m.id) ? "" : "unsaved"}>
              <div className="saved-where">
                <button className="link" onClick={() => navigate(m.thread_root_id ? `/c/${m.channel_id}/t/${m.thread_root_id}` : `/c/${m.channel_id}/m/${m.id}`)}>
                  {session.channelName(m.channel_id) || t("a conversation")}
                  {m.thread_root_id ? " · " + t("in a thread") : ""}
                </button>
                <span className="muted">{t("saved {date}", { date: new Date(saved_at).toLocaleDateString(locale, { month: "short", day: "numeric" }) })}</span>
              </div>
              <MessageItem m={m} />
            </li>
          ))}
        </ul>
      )}
      {more && (
        <div className="form-actions">
          <button onClick={() => void loadMore()}>{t("Show older")}</button>
        </div>
      )}
    </Page>
  );
}
